import React from "react";
import { HeartHandshake, Eye, Sparkles, MapPin, Brush, Handshake } from "lucide-react";
import Reveal from "../components/site/Reveal";
import { LogoTitle, SectionTitle, CtaLink } from "../components/site/Common";

const STORY = [
  "Ouest Conciergerie est née d'une évidence : sur la Côte d'Émeraude, les propriétaires ont besoin d'une équipe proche, fiable et disponible pour louer sereinement leur logement. Nous avons grandi ici, entre les remparts de Saint-Malo, les villas de Dinard et les ports de Cancale. Ce territoire, nous le connaissons rue par rue.",
  "Cette connaissance du terrain change tout. Nous savons quand la ville se remplit pour la Route du Rhum ou les festivals d'été, quels quartiers séduisent les familles, et à quel moment ajuster les prix. Nous intervenons vite, parce que nous ne sommes jamais loin.",
  "Nous avons fait le choix de l'exigence de l'hôtellerie : ménage soigné, linge impeccable, accueil chaleureux et voyageurs suivis du début à la fin de leur séjour. C'est ce qui fait les bons avis, et les bons avis font les réservations.",
];

export const FOUNDERS = [
  { name: "Malo", photo: "/photos/fondateur-malo.jpg?v=4", role: "Co-fondateur", quote: "On a lancé Ouest Conciergerie pour offrir aux propriétaires d'ici ce qu'on aurait aimé trouver nous-mêmes : une équipe du coin, joignable, qui traite chaque logement comme s'il était le sien." },
  { name: "Nolwenn", photo: "/photos/fondatrice-nolwenn.jpg?v=4", role: "Co-fondatrice", quote: "Notre promesse est simple : des règles claires, des revenus versés directement par les plateformes et un accueil digne d'un bel hôtel. Le reste, c'est du terrain et de la rigueur, tous les jours." },
];

const VALUES = [
  { icon: HeartHandshake, title: "La proximité avant tout", text: "Une équipe qui vit sur la Côte d'Émeraude, à quelques minutes de votre logement, et un interlocuteur qui vous répond vraiment." },
  { icon: Eye, title: "Des règles claires", text: "Une commission simple, aucun frais caché, vos revenus versés directement sur votre compte : vous savez toujours où vous en êtes." },
  { icon: Sparkles, title: "L'exigence de l'hôtellerie", text: "Chaque séjour est préparé avec le soin d'un hôtel, pour des voyageurs ravis, de meilleurs avis et un calendrier bien rempli." },
];

const TEAM = [
  { icon: MapPin, title: "Notre équipe terrain", text: "Accueil des voyageurs, remise des clés, petites interventions : des personnes du secteur, formées à nos standards, qui connaissent chaque commune." },
  { icon: Brush, title: "Nos prestataires ménage", text: "Des professionnels du ménage et du linge, sélectionnés avec soin, qui remettent votre logement en état impeccable entre chaque séjour." },
  { icon: Handshake, title: "Nos partenaires", text: "Artisans, blanchisseries et commerçants locaux de confiance, pour garantir un haut niveau de qualité dans chaque logement." },
];

function Card({ icon: I, title, text, dark, testid }) {
  return (
    <div data-testid={testid} className={`rounded-[28px] p-7 lg:p-8 h-full ring-1 transition-transform duration-300 hover:-translate-y-1 ${dark ? "bg-[#1c3427] ring-white/10" : "bg-white ring-[#e6dcc8]"}`}>
      <span className={`w-12 h-12 rounded-2xl grid place-items-center ${dark ? "bg-[#d9895a] text-[#13261c]" : "bg-[#f6e3d6] text-[#c4703f]"}`}><I className="w-6 h-6" /></span>
      <p className={`mt-5 font-logo font-black text-xl ${dark ? "text-[#faf7f0]" : "text-[#13261c]"}`}>{title}</p>
      <p className={`mt-3 leading-relaxed ${dark ? "text-white/75" : "text-[#5a6158]"}`}>{text}</p>
    </div>
  );
}

function Founders() {
  return (
    <section className="bg-[#13261c] grain relative overflow-hidden" data-testid="about-founders">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 relative">
        <h1 className="sr-only">Qui sommes-nous : Nolwenn et Malo, fondateurs d'Ouest Conciergerie</h1>
        <SectionTitle light kicker="Qui sommes-nous · Les fondateurs" title="deux passionnés" script="une même ambition" text="Nolwenn et Malo, passionnés d'accueil et d'immobilier et originaires de la Côte d'Émeraude, ont imaginé une conciergerie à taille humaine, ancrée dans son territoire et tournée vers la qualité." />
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {FOUNDERS.map((f, i) => (
            <Reveal key={f.name} delay={i * 120}>
              <figure className="rounded-[28px] bg-[#1c3427] ring-1 ring-white/10 p-6 lg:p-8 h-full grid sm:grid-cols-[minmax(0,200px)_1fr] gap-6 lg:gap-8 items-center" data-testid={`about-founder-${i}`}>
                {f.photo ? <img loading="lazy" decoding="async" src={f.photo} alt={f.name} className="w-full max-w-[200px] aspect-[3/4] object-cover rounded-[20px]" />
                  : <div className="w-full max-w-[140px] sm:max-w-[200px] aspect-[3/4] rounded-[20px] bg-gradient-to-br from-[#2a4636] to-[#13261c] ring-1 ring-white/10 grid place-items-center">
                      <span className="font-logo font-black text-7xl text-[#d9895a]">{f.name[0]}</span>
                    </div>}
                <div>
                  <figcaption><span className="block font-logo font-black text-2xl text-[#faf7f0]">{f.name}</span><span className="block mt-1 text-xs font-bold uppercase tracking-[0.25em] text-[#d9895a]">{f.role}</span></figcaption>
                  <blockquote className="mt-4 font-display italic text-lg lg:text-xl text-white/85 leading-relaxed">« {f.quote} »</blockquote>
                </div>
              </figure>
            </Reveal>
          ))}
        </div>
        <div className="mt-14 grid lg:grid-cols-3 gap-8 text-white/75 leading-relaxed" data-testid="about-story">
          {STORY.map((p, i) => <Reveal key={i} delay={i * 80}><p className={i === 0 ? "text-white/90" : ""}>{p}</p></Reveal>)}
        </div>
      </div>
    </section>
  );
}

function Grid({ kicker, title, script, text, items, testid }) {
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28" data-testid={testid}>
      <SectionTitle kicker={kicker} title={title} script={script} text={text} />
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {items.map((v, i) => <Reveal key={v.title} delay={i * 100}><Card {...v} testid={`${testid}-${i}`} /></Reveal>)}
      </div>
    </section>
  );
}

export default function QuiSommesNous() {
  return (
    <div data-testid="about-page" className="bg-gradient-to-b from-[#f3ede1] to-[#faf7f0]">
      <Founders />
      <Grid testid="about-values" kicker="Nos valeurs" title="ce qui nous guide" script="au quotidien" text="Trois engagements partagés par toute l'équipe, pour que chaque propriétaire se sente épaulé et chaque voyageur bien reçu." items={VALUES} />
      <div className="border-t border-[#e6dcc8]" />
      <Grid testid="about-team" kicker="Une équipe soudée" title="tous mobilisés" script="pour votre logement" text="Derrière chaque séjour réussi, des personnes qui travaillent main dans la main, séjour après séjour." items={TEAM} />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24 text-center">
        <Reveal><p className="font-script text-3xl lg:text-4xl text-[#d9895a]">on s'occupe de tout, vous profitez</p></Reveal>
        <Reveal delay={100} className="mt-8 flex flex-wrap justify-center gap-3"><CtaLink to="/proprietaires" testid="about-cta-simulate">Simuler mes revenus</CtaLink><CtaLink to="/contact" variant="ghost" testid="about-cta-contact">Nous contacter</CtaLink></Reveal>
      </section>
    </div>
  );
}
